import React, { useEffect, useState } from "react";
import { Checkbox, Col, Form, Input, Row, Tabs } from "antd";
import { useTranslation } from "react-i18next";
import GeneralSelect from "../../../../components/selects/general/GeneralSelect";
import { SelectProductType } from "../../../../components/selects";
import { EntBasicHB } from "../../../../models/handBooks/basicHB";
import { EntInitialFilter } from "../../../../models/filters/initialFilter";
import { Profile } from "../../../../models/organic/processingCapacity";
import { useLazyGetIndustryTypeListQuery } from "../../../../redux/api/handBooksApi/organic/industryTypeApi";
import { useLazyGetRawMaterialListQuery } from "../../../../redux/api/handBooksApi/organic/rawMaterialApi";
import { useLazyGetSeasonalityListQuery } from "../../../../redux/api/handBooksApi/organic/seasonalityApi";
import { useLazyGetCertificateListQuery } from "../../../../redux/api/handBooksApi/organic/certificateApi";
import SelectCitizenship from "../../../../components/selects/SelectCitizenship";

interface IProps {
  items: Profile[]|null;
  setItems: (profiles: Profile[]) => void;
}

export const ProcessingCapacityItems: React.FC<IProps> = ({ items, setItems }) => {
  const { t } = useTranslation();

  const [tabs, setTabs] = useState<Profile[]>([]);
  const [activeKey, setActiveKey] = useState<number>(0);

  const generateUniqueKey = (): number => Date.now() + Math.floor(Math.random() * 1000);

  const createNewProfile = (): Profile => ({
	key: generateUniqueKey(),
	id: null,
	processingCapacityRegisterId: null,
	industryTypeId: null,
	rawMaterialId: null,
	productTypeId: null,
	tonnesPerDay: null,
	storageCapacityTonnes: null,
	seasonalityId: null,
	equipmentDescription: null,
	certificateId: null,
	isItExport: false,
	destinationCountryId: null,
  });

  useEffect(() => {
	if (items && items.length > 0) {
	  const initialized = items.map((p) => ({
		...p,
		key: p.key ?? generateUniqueKey(),
	  }));
	  setTabs(initialized);
	  setActiveKey(initialized[0].key!);
	} else {
	  const defaultTab = createNewProfile();
	  setTabs([defaultTab]);
	  setActiveKey(defaultTab.key!);
	}
  }, []);

  useEffect(() => {
	setItems(tabs);
  }, [tabs]);

  const handleAdd = () => {
	const newTab = createNewProfile();
	const updated = [...tabs, newTab];
	setTabs(updated);
	setActiveKey(newTab.key!);
  };

  const handleDelete = (key: number) => {
	const newTabs = tabs.filter((tab) => tab.key !== key);
	setTabs(newTabs);
	setItems(newTabs);
	if (newTabs.length > 0) {
	  setActiveKey(newTabs[0].key!);
	}
  };

  const handleChange = (index: number, field: keyof Profile, value: any) => {
	const updated = [...tabs];
	updated[index] = { ...updated[index], [field]: value };
	setTabs(updated);
  };

  return (
	<Tabs
	  type="editable-card"
	  activeKey={ activeKey.toString() }
	  onChange={ (key) => setActiveKey(Number(key)) }
	  onEdit={ (key, action) => {
		if (action === "add") handleAdd();
		else if (action === "remove") handleDelete(Number(key));
	  } }
	  items={ tabs.map((profile, index) => ({
		key: profile.key!.toString(),
		label: `${ t("Профиль") } #${ index + 1 }`,
		children: (
		  <Form layout="vertical">
			<Row gutter={ 16 } key="step1">
			  <Col span={ 12 }>
				<Form.Item label={ t("Отрасль производства") }>
				  <GeneralSelect<EntBasicHB, EntInitialFilter>
					queryHook={ useLazyGetIndustryTypeListQuery }
					initialFilter={ new EntInitialFilter() }
					value={ profile.industryTypeId || profile.industryType?.id }
					onChange={ (val) => handleChange(index, "industryTypeId", val) }
				  />
				</Form.Item>
				<Form.Item label={ t("Сырье") }>
				  <GeneralSelect<EntBasicHB, EntInitialFilter>
					queryHook={ useLazyGetRawMaterialListQuery }
					initialFilter={ new EntInitialFilter() }
					value={ profile.rawMaterialId || profile.rawMaterial?.id }
					onChange={ (val) => handleChange(index, "rawMaterialId", val) }
				  />
				</Form.Item>
				<Form.Item label={ t("Тип продукции") }>
				  <SelectProductType
					value={ profile.productTypeId || profile.productType?.id }
					onChange={ (val) => handleChange(index, "productTypeId", val) }
				  />
				</Form.Item>
				<Form.Item label={ t("Тонн в день") }>
				  <Input
					value={ profile.tonnesPerDay ?? "" }
					onChange={ (e) =>
					  handleChange(index, "tonnesPerDay", Number(e.target.value))
					}
				  />
				</Form.Item>
				<Form.Item label={ t("Объем хранения (тонн)") }>
				  <Input
					value={ profile.storageCapacityTonnes ?? "" }
					onChange={ (e) =>
					  handleChange(index, "storageCapacityTonnes", Number(e.target.value))
					}
				  />
				</Form.Item>
			  </Col>
			  <Col span={ 12 }>
				<Form.Item label={ t("Сезонность") }>
				  <GeneralSelect<EntBasicHB, EntInitialFilter>
					queryHook={ useLazyGetSeasonalityListQuery }
					initialFilter={ new EntInitialFilter() }
					value={ profile.seasonalityId || profile.seasonality?.id }
					onChange={ (val) => handleChange(index, "seasonalityId", val) }
				  />
				</Form.Item>
				<Form.Item label={ t("Технологическая база") }>
				  <Input
					value={ profile.equipmentDescription ?? "" }
					onChange={ (e) =>
					  handleChange(index, "equipmentDescription", e.target.value)
					}
				  />
				</Form.Item>
				<Form.Item label={ t("Сертификат") }>
				  <GeneralSelect<EntBasicHB, EntInitialFilter>
					queryHook={ useLazyGetCertificateListQuery }
					initialFilter={ new EntInitialFilter() }
					value={ profile.certificateId || profile.certificate?.id }
					onChange={ (val) => handleChange(index, "certificateId", val) }
				  />
				</Form.Item>
				<Form.Item label={ t("Страна назначения") }>
				  <SelectCitizenship
					value={ profile.destinationCountryId || profile.destinationCitizenship?.id }
					onChange={ (val) => handleChange(index, "destinationCountryId", val) }
				  />
				</Form.Item>
				<Form.Item label={ t("Экспорт?") }>
				  <Checkbox
					checked={ profile.isItExport ?? false }
					onChange={ (e) => handleChange(index, "isItExport", e.target.checked) }
				  >
					{ t("Да") }
				  </Checkbox>
				</Form.Item>
			  </Col>
			</Row>
		  </Form>
		),
	  })) }
	/>
  );
};
